import { useSubscriptions } from './useSubscriptions'
import { SubscriptionType } from '@/types/subscription'
import { STRIPE_INTERVAL } from '@/constants/subscription'

export interface StructureOfferPrice {
  id: string
  amount: number
  currency: string
  discount_percent?: string
}

export interface StructureOffer {
  id: string
  name: string
  description: string | null
  licenses: number | null
  prices: {
    month?: StructureOfferPrice
    year?: StructureOfferPrice
  }
}

function extractLicensesCount(name: string, metadataLicenses?: number | string): number | null {
  if (metadataLicenses) {
    const parsed =
      typeof metadataLicenses === 'string' ? parseInt(metadataLicenses, 10) : metadataLicenses
    if (!isNaN(parsed) && parsed > 0) {
      return parsed
    }
  }

  const match = name.match(/(\d+)\s*(?:sièges?|licences?|utilisateurs?)/i)
  if (match) {
    const count = parseInt(match[1], 10)
    if (!isNaN(count) && count > 0) {
      return count
    }
  }

  return null
}

export const useStructureSubscriptions = () => {
  const { subscriptions, isLoading, isError, error } = useSubscriptions()

  const structureSubscriptions = subscriptions.filter(sub => {
    return sub.type === SubscriptionType.STRUCTURE && sub.name !== 'Offre sur mesure'
  })

  const offersById = new Map<
    string,
    {
      id: string
      name: string
      description: string | null
      licenses: number | null
      prices: StructureOffer['prices']
    }
  >()

  for (const sub of structureSubscriptions) {
    const licenses = extractLicensesCount(sub.name, sub.licenses)
    const priceEntry: StructureOfferPrice = {
      id: sub.price.id,
      amount: sub.price.amount,
      currency: sub.price.currency,
      ...(sub.price.discount_percent != null &&
        sub.price.discount_percent !== '' && {
          discount_percent: String(sub.price.discount_percent),
        }),
    }

    const existing = offersById.get(sub.id)
    if (existing) {
      if (sub.interval === STRIPE_INTERVAL.MONTH) {
        existing.prices.month = priceEntry
      } else {
        existing.prices.year = priceEntry
      }
    } else {
      offersById.set(sub.id, {
        id: sub.id,
        name: sub.name,
        description: sub.description,
        licenses,
        prices: {
          month: sub.interval === STRIPE_INTERVAL.MONTH ? priceEntry : undefined,
          year: sub.interval === STRIPE_INTERVAL.YEAR ? priceEntry : undefined,
        },
      })
    }
  }

  const offers: StructureOffer[] = Array.from(offersById.values()).sort((a, b) => {
    if (a.licenses === null) return 1
    if (b.licenses === null) return -1
    return a.licenses - b.licenses
  })

  const maxLicensesFromOffers =
    offers.length > 0 ? Math.max(...offers.map(o => o.licenses ?? 0)) : null

  const customOffer: StructureOffer = {
    id: 'custom-offer',
    name: 'Offre sur mesure',
    description: null,
    licenses: null,
    prices: {},
  }

  return {
    offers: [...offers, customOffer],
    maxLicensesFromOffers,
    isLoading,
    isError,
    error,
  }
}
